import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch, match, useHistory, useRouteMatch } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faBuilding, faChevronLeft, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Content from 'assets/js/components/Content';
import { ContactAddressFormDrawer } from 'assets/js/pages/contacts/ContactAddressFormDrawer';
import { ContactIntegrations } from 'assets/js/pages/contacts/ContactIntegrations';
import * as ContactSharedActions from 'assets/js/pages/contacts/ContactSharedActions';
import { ContactSidebarGivingSummaryChart } from 'assets/js/pages/contacts/ContactSidebarGivingSummaryChart';
import * as ContactSidebarPrimaryInfo from 'assets/js/pages/contacts/ContactSidebarPrimaryInfo';
import { ContactSidebarTags } from 'assets/js/pages/contacts/ContactSidebarTags';
import { ContactSummaries } from 'assets/js/pages/contacts/ContactSummaries';
import { ConvertContactFormDrawer } from 'assets/js/pages/contacts/ConvertContactFormDrawer';
import { EditContactFormDrawer } from 'assets/js/pages/contacts/EditContactFormDrawer';
import * as AssociateContactDialog from 'assets/js/pages/contacts/companies/AssociateContactDialog';
import { CompanyContacts } from 'assets/js/pages/contacts/companies/CompanyContacts';
import { CompanyHeader } from 'assets/js/pages/contacts/companies/CompanyHeader';
import { CompanyPrimaryInfoActions } from 'assets/js/pages/contacts/companies/CompanyPrimaryInfoActions';
import { CompanyProfile } from 'assets/js/pages/contacts/companies/CompanyProfile';
import { CopyAllEmails } from 'assets/js/pages/contacts/companies/CopyAllEmails';
import ActivityFeed from 'assets/js/pages/contacts/single/timeline';
import { TransactionDrawerRouter } from 'assets/js/pages/transactions/TransactionDrawerRouter';
import { TransactionsDetailsFormDrawer } from 'assets/js/pages/transactions/TransactionsOfflineFormDrawer';
import { DrawerRoute } from 'components/drawer-route';
import * as DropdownMenu from 'components/dropdown-menu';
import { handleRateLimitError } from 'components/error-boundaries/RateLimitErrorBoundary';
import * as NavMenu from 'components/nav-menu';
import * as ExportContactTransactionsReportModal from 'components/pages/contacts/components/ExportContactTransactionsReportModal';
import * as ExportSuccessAlert from 'components/pages/contacts/components/ExportSuccessAlert';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { useCurrentContact } from 'customHooks/useCurrentContact';

const Company = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const { data: company } = useCurrentContact();
    const { accountId } = useCurrentAccount();
    const { data } = useCurrentAccountIntegrations();
    const { donorSearch } = data || {};
    const isDonorSearchEnabled = !!donorSearch?.active;
    const [showExportTransactionsReportsModal, setShowExportTransactionsReportsModal] =
        useState(false);
    const [showExportSuccessModal, setShowExportSuccessModal] = useState(false);

    if (company.type === ContactType.Individual) {
        return <Redirect to={`/accounts/${accountId}/contacts/${company.id}`} />;
    }

    return (
        <div className={'flex flex-col gap-y-6'}>
            <div>
                <Button
                    variant="none"
                    size="sm"
                    className="font-medium text-gray-600"
                    onClick={() => history.push(`/accounts/${accountId}/companies`)}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    <p>Back to companies</p>
                </Button>
            </div>
            <div className={'flex flex-wrap items-end justify-between gap-4'}>
                <CompanyHeader>
                    {company.company_name}{' '}
                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-500">
                        <FontAwesomeIcon icon={faBuilding} /> Company
                    </span>
                </CompanyHeader>
                <div className={'flex w-full items-center justify-between gap-2 sm:w-fit'}>
                    <CompanyPrimaryInfoActions />
                    <div className="flex gap-4">
                        <ContactSharedActions.Root>
                            <ContactSharedActions.AddTransaction />
                            <ContactSharedActions.ExportTransactions
                                onSelect={() => setShowExportTransactionsReportsModal(true)}
                            />
                            <ContactSharedActions.ToggleArchived />
                            <ContactSharedActions.Delete />
                            <Suspense
                                fallback={
                                    <DropdownMenu.Item disabled>Copy all emails</DropdownMenu.Item>
                                }
                            >
                                <CopyAllEmails>Copy all emails</CopyAllEmails>
                            </Suspense>
                            <DropdownMenu.Item
                                onClick={() => {
                                    history.push({
                                        pathname: `/accounts/${accountId}/companies/${company.id}/convert`,
                                    });
                                }}
                            >
                                Convert to individual
                            </DropdownMenu.Item>
                        </ContactSharedActions.Root>

                        {!!company && (
                            <ExportContactTransactionsReportModal.Root
                                contactId={company.id}
                                open={showExportTransactionsReportsModal}
                                onClose={() => setShowExportTransactionsReportsModal(false)}
                                onSuccess={() => setShowExportSuccessModal(true)}
                            />
                        )}

                        <ExportSuccessAlert.Root
                            open={showExportSuccessModal}
                            onClose={() => setShowExportSuccessModal(false)}
                        />
                        <AssociateContactDialog.Root>
                            <AssociateContactDialog.Trigger asChild>
                                <Button variant="solid">
                                    <FontAwesomeIcon icon={faPlus} /> Add contact
                                </Button>
                            </AssociateContactDialog.Trigger>
                        </AssociateContactDialog.Root>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-12 gap-8'}>
                <div className={'col-span-4 hidden sm:block'}>
                    <div className={'flex flex-col gap-4'}>
                        <ContactSidebarPrimaryInfo.Root>
                            <ContactSidebarPrimaryInfo.CompanyEmail />
                            <ContactSidebarPrimaryInfo.CompanyPhone />
                            <ContactSidebarPrimaryInfo.Address />
                            <ContactSidebarPrimaryInfo.Socials />
                        </ContactSidebarPrimaryInfo.Root>
                        <ContactSidebarGivingSummaryChart />
                        <ContactSidebarTags key={company.id} />
                    </div>
                </div>
                <div className={'col-span-12 sm:col-span-8'}>
                    <div className={'flex flex-col gap-8'}>
                        <NavMenu.Root>
                            <NavMenu.Item exact={true} to={`${match.url}`}>
                                Contacts
                            </NavMenu.Item>
                            <NavMenu.Item to={`${match.url}/profile`}>Profile</NavMenu.Item>
                            <NavMenu.Item exact={true} to={`${match.url}/activity`}>
                                Activity
                            </NavMenu.Item>
                            <NavMenu.Item to={`${match.url}/summaries`}>Summaries</NavMenu.Item>
                            {isDonorSearchEnabled ? (
                                <NavMenu.Item to={`${match.url}/integrations`}>
                                    Integrations
                                </NavMenu.Item>
                            ) : null}
                        </NavMenu.Root>
                        <Suspense fallback={<Loader />}>
                            <Switch>
                                <Route path={`${match.path}/activity`}>
                                    {({ match }) => (
                                        <>
                                            <DrawerRoutes match={match} />
                                            <Switch>
                                                <Route
                                                    path={match?.path}
                                                    component={ActivityFeed}
                                                />
                                            </Switch>
                                        </>
                                    )}
                                </Route>
                                <Route path={`${match.path}/summaries`}>
                                    {({ match }) => (
                                        <>
                                            <DrawerRoutes match={match} />
                                            <Switch>
                                                <Route
                                                    path={match?.path}
                                                    component={ContactSummaries}
                                                />
                                            </Switch>
                                        </>
                                    )}
                                </Route>
                                <Route path={`${match.path}/profile`}>
                                    {({ match }) => (
                                        <>
                                            <DrawerRoutes match={match} />
                                            <Switch>
                                                <Route
                                                    path={match?.path}
                                                    component={CompanyProfile}
                                                />
                                            </Switch>
                                        </>
                                    )}
                                </Route>
                                {isDonorSearchEnabled ? (
                                    <Route path={`${match.path}/integrations`}>
                                        {({ match }) => (
                                            <>
                                                <DrawerRoutes match={match} />
                                                <Switch>
                                                    <Route
                                                        path={match?.path}
                                                        component={ContactIntegrations}
                                                    />
                                                </Switch>
                                            </>
                                        )}
                                    </Route>
                                ) : null}
                                <Route path={match.path}>
                                    {({ match }) => (
                                        <>
                                            <DrawerRoutes match={match} />
                                            <Switch>
                                                <Route
                                                    path={match?.path}
                                                    component={CompanyContacts}
                                                />
                                            </Switch>
                                        </>
                                    )}
                                </Route>
                            </Switch>
                        </Suspense>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DrawerRoutes = ({ match }: { match: match | null }) => {
    if (!match) {
        return null;
    }

    return (
        <>
            <DrawerRoute path={`${match.path}/convert`} component={ConvertContactFormDrawer} />
            <DrawerRoute path={[`${match.path}/details/edit`]} component={EditContactFormDrawer} />
            <DrawerRoute
                path={[`${match.path}/addresses/new`, `${match.path}/addresses/:address_id/edit`]}
                component={ContactAddressFormDrawer}
            />
            <Switch>
                <DrawerRoute
                    path={`${match.path}/transactions/new`}
                    component={TransactionsDetailsFormDrawer}
                />
                <DrawerRoute
                    path={`${match.path}/transactions/:transaction_id`}
                    component={TransactionDrawerRouter}
                />
            </Switch>
        </>
    );
};

const CompanyWrapper = () => {
    const { accountId } = useCurrentAccount();
    return (
        <ErrorBoundary
            onError={handleRateLimitError}
            fallback={<Redirect to={`/accounts/${accountId}/companies`} />}
        >
            <Content className="max-w-5xl">
                <Company />
            </Content>
        </ErrorBoundary>
    );
};

export { CompanyWrapper as Company };
